// Order sets (PROD-2723, PROD-2832): keeping the applications of a set to one visit from piling
// up. PURE.
//
// Found live on dev after PROD-2721 (1 Oct): every application of a set left its own
// "Not ordered from <set>" list and its own "No medication checks" notice, and both outlived the
// orders they were about. PROD-2723 fixed that for sets linked to a diagnosis; PROD-2832 (2 Oct)
// found the same pile while the orders WAIT in "Needs a diagnosis", and "already ordered" chips
// naming orders the clinician had since removed. So:
//   · ONE leftovers list per set per diagnosis — and per set while waiting for one. A later
//     application (or a "Needs a diagnosis" group resolved onto that diagnosis) takes over the
//     earlier lists, merged by item;
//   · the set's list (and its notice) goes once every order its applications placed or held
//     waiting is gone;
//   · "already ordered" is re-checked live against the canvas AND the waiting orders: an item whose
//     match is gone is listed to add again, never as already ordered;
//   · the notice follows the medications actually on the visit (or waiting), not the history.
// What counts as a duplicate stays with `order-set-duplicates.ts` (PROD-2721).
import type { OrderPayloadMap } from '../lib/order-payload';
import type { PlanOrder } from '../lib/to-patient-assessment';
import type { DraftOrder, DuplicateItem, ListedItem, NotOrderableItem } from './order-set-apply';

/** The fields of an application these rules read and write. */
export interface ApplicationLike {
  key: string;
  setId: string;
  dxId: string | null;
  pending: DraftOrder[];
  optional: ListedItem[];
  duplicates: DuplicateItem[];
  notOrderable: NotOrderableItem[];
  unsupported: ListedItem[];
  listHidden: boolean;
  placesMedication: boolean;
  undoReceipt: unknown | null;
  /**
   * Every order this application ever placed on the canvas OR held waiting in "Needs a diagnosis"
   * (apply, "Add", "Order anyway", link). A waiting draft keeps its id when it is placed.
   */
  ownOrderIds: string[];
}

const LISTS = ['optional', 'duplicates', 'notOrderable', 'unsupported'] as const;
const EMPTY_LISTS = { optional: [], duplicates: [], notOrderable: [], unsupported: [] };

/** The pile an application belongs to: its set, and its diagnosis or "waiting for one". */
export function pileOf(app: Pick<ApplicationLike, 'setId' | 'dxId'>): string {
  return `${app.setId}\u0000${app.dxId ?? '\u0000waiting'}`;
}

function listedIds(app: ApplicationLike): Set<string> {
  return new Set(LISTS.flatMap((k) => app[k].map((i) => i.itemId)));
}

/**
 * One list per pile (set + diagnosis, or set + waiting): among the live applications of the same
 * pile, the LATEST (last in the array) keeps a list that is the union of theirs — its own items
 * first, then each earlier item it does not already list — and the earlier ones list nothing.
 * Undone applications are left alone. A waiting application keeps its own waiting orders (only
 * lists move).
 */
export function consolidateLeftovers<T extends ApplicationLike>(apps: readonly T[]): T[] {
  const latestByPile = new Map<string, number>();
  apps.forEach((a, i) => {
    if (!a.undoReceipt) latestByPile.set(pileOf(a), i);
  });
  const next = [...apps];
  for (const [pile, target] of latestByPile) {
    let merged = { ...next[target] };
    let changed = false;
    next.forEach((a, i) => {
      if (i === target || a.undoReceipt || pileOf(a) !== pile) return;
      if (!a.listHidden) {
        const have = listedIds(merged);
        for (const k of LISTS) {
          const extra = (a[k] as ListedItem[]).filter((item) => !have.has(item.itemId));
          if (extra.length > 0) merged = { ...merged, [k]: [...merged[k], ...extra] };
        }
      }
      if (LISTS.some((k) => a[k].length > 0)) {
        next[i] = { ...a, ...EMPTY_LISTS };
        changed = true;
      }
    });
    if (changed) next[target] = merged;
  }
  return next;
}

/**
 * When a "Needs a diagnosis" group is linked to `dxId`, the other applications of the SAME set
 * that wait with nothing pending (every order of theirs was already on the visit, so only their
 * list remains) follow it there — they were about the same missing diagnosis.
 */
export function followResolvedDiagnosis<T extends ApplicationLike>(apps: readonly T[], setId: string, dxId: string): T[] {
  return apps.map((a) =>
    a.setId === setId && !a.dxId && !a.undoReceipt && a.pending.length === 0 ? { ...a, dxId } : a,
  );
}

export interface LiveContext {
  orders: readonly PlanOrder[];
  payloads: OrderPayloadMap;
  /**
   * Is this listed duplicate STILL on the visit (canvas or waiting)? The host answers with the
   * PROD-2721 rules; absent → every duplicate is taken as still standing.
   */
  isStillDuplicate?: (app: ApplicationLike, dup: DuplicateItem) => boolean;
}

/**
 * The applications as the A/P should show them NOW:
 *  - a pile whose applications placed or held orders, every one of them now gone (removed from the
 *    canvas, none waiting), shows no list and no notice;
 *  - otherwise `placesMedication` is true only while one of the application's medications is on
 *    the canvas or waiting;
 *  - a duplicate whose match is gone is listed to add again ("+"), never as "already ordered".
 * A pile that placed nothing (everything was already ordered by hand) keeps its list: "Order
 * anyway" is the point of it, and its chips re-check like any other.
 */
export function liveApplications<T extends ApplicationLike>(apps: readonly T[], ctx: LiveContext): T[] {
  const onCanvas = new Set(ctx.orders.map((o) => o.id));
  const waitingIds = new Set(apps.flatMap((a) => (a.undoReceipt ? [] : a.pending.map((d) => d.planOrder.id))));
  const alive = (id: string) => onCanvas.has(id) || waitingIds.has(id);

  const gonePiles = new Set<string>();
  const byPile = new Map<string, T[]>();
  for (const a of apps) {
    if (a.undoReceipt) continue;
    byPile.set(pileOf(a), [...(byPile.get(pileOf(a)) ?? []), a]);
  }
  for (const [pile, members] of byPile) {
    const own = members.flatMap((a) => a.ownOrderIds);
    if (own.length > 0 && !own.some(alive)) gonePiles.add(pile);
  }

  return apps.map((app) => {
    if (app.undoReceipt) return app;
    if (gonePiles.has(pileOf(app))) {
      const hasList = LISTS.some((k) => app[k].length > 0);
      return hasList || app.placesMedication ? { ...app, ...EMPTY_LISTS, placesMedication: false } : app;
    }
    const placesMedication =
      app.pending.some((d) => d.payload.kind === 'medication') ||
      app.ownOrderIds.some((id) => onCanvas.has(id) && ctx.payloads.get(id)?.kind === 'medication');
    const stale = ctx.isStillDuplicate ? app.duplicates.filter((d) => !ctx.isStillDuplicate!(app, d)) : [];
    if (placesMedication === app.placesMedication && stale.length === 0) return app;
    if (stale.length === 0) return { ...app, placesMedication };
    const staleIds = new Set(stale.map((d) => d.itemId));
    return {
      ...app,
      placesMedication,
      duplicates: app.duplicates.filter((d) => !staleIds.has(d.itemId)),
      optional: [...app.optional, ...stale.map(({ itemId, display }) => ({ itemId, display }))],
    };
  });
}

/** One application on its own (its own pile). */
export function liveApplication<T extends ApplicationLike>(app: T, orders: readonly PlanOrder[], payloads: OrderPayloadMap): T {
  return liveApplications([app], { orders, payloads })[0];
}
